class GalleryShader {
  /**
   * @param {KeenSliderInstance} slider
   * @param {{ intensity?: number, band?: number }} options
   */
  constructor (slider, options = {}) {
    this.slider = slider
    this.options = options
    this.ready = false
    this.started = false
    this.visible = true
    this.textures = []
    this.frame = null
    this.boundFrom = -1
    this.boundTo = -1
    this.lastProgress = -1
    this.debug = location.search.indexOf('shaderdebug') !== -1

    this.render = this.render.bind(this)
    this.draw = this.draw.bind(this)
    this.resize = this.resize.bind(this)
    this.handleIntersect = this.handleIntersect.bind(this)
    this.handleVisibility = this.handleVisibility.bind(this)
  }

  init () {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const images = this.collectImages()
    if (!images || images.length < 2) return
    if (this.maxIdx < 1) return

    this.images = images
    document.addEventListener('visibilitychange', this.handleVisibility)

    if (!window.IntersectionObserver) return this.start()

    this.viewport = new IntersectionObserver(this.handleIntersect, {
      rootMargin: GalleryShader.preloadMargin,
    })
    this.viewport.observe(this.slider.container)
  }

  handleIntersect (entries) {
    const entry = entries[entries.length - 1]
    this.visible = entry.isIntersecting

    if (!this.visible) {
      if (this.frame) { cancelAnimationFrame(this.frame); this.frame = null }
      return
    }

    if (!this.started) return this.start()
    this.render()
  }

  handleVisibility () {
    if (document.visibilityState !== 'visible') {
      if (this.frame) { cancelAnimationFrame(this.frame); this.frame = null }
      return
    }
    this.render()
  }

  start () {
    this.started = true
    if (!this.initGL()) return this.log('init failed — no webgl context')
    this.log('init — ' + this.images.length + ' slides')
    this.loadTextures()
  }

  collectImages () {
    const images = []

    for (const slide of this.slider.slides) {
      const img = slide.querySelector('img')
      if (!img) return null
      images.push(img)
    }

    return images
  }

  initGL () {
    const canvas = document.createElement('canvas')
    canvas.className = 'prd-Media_Canvas'
    canvas.setAttribute('aria-hidden', 'true')

    const gl = canvas.getContext('webgl', {
      premultipliedAlpha: false,
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: 'low-power',
      failIfMajorPerformanceCaveat: true,
    })
    if (!gl) return false

    this.canvas = canvas
    this.gl = gl

    this.onContextLost = (e) => { e.preventDefault(); this.handleContextLost() }
    this.onContextRestored = () => this.handleContextRestored()
    canvas.addEventListener('webglcontextlost', this.onContextLost, false)
    canvas.addEventListener('webglcontextrestored', this.onContextRestored, false)

    this.maxTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE)
    const highp = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT)
    this.highpAvailable = !!(highp && highp.precision > 0)

    if (this.debug) {
      const info = gl.getExtension('WEBGL_debug_renderer_info')
      if (info) this.log('gpu: ' + gl.getParameter(info.UNMASKED_RENDERER_WEBGL))
      this.log('maxTexture: ' + this.maxTexture + ' highp: ' + this.highpAvailable)
      this.log('textureTarget: ' + this.textureTarget)
    }

    const container = this.slider.container
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative'
    }
    container.appendChild(canvas)

    return true
  }

  loadTextures () {
    let pending = this.images.length
    const sources = []

    this.target = this.textureTarget

    const done = () => { if (--pending === 0) this.build(sources) }

    this.images.forEach((img, index) => {
      const loaded = new Image()
      loaded.crossOrigin = 'anonymous'
      loaded.onload = done
      loaded.onerror = () => this.fail('image load error')
      loaded.src = this.bestSource(img)
      sources[index] = loaded
    })
  }

  /**
   * Picks the smallest srcset entry that still covers the rendered size, so the
   * gallery does not upload nine full-resolution textures on a phone.
   */
  bestSource (img) {
    const fallback = img.currentSrc || img.src
    const set = img.srcset
    if (!set) return fallback

    const target = this.target

    const candidates = set.split(',')
      .map(part => {
        const [url, w] = part.trim().split(/\s+/)
        return { url, w: parseInt(w, 10) || 0 }
      })
      .filter(candidate => candidate.url && candidate.w > 0)
      .sort((a, b) => a.w - b.w)

    if (!candidates.length) return fallback

    const fit = candidates.find(candidate => candidate.w >= target)
    return (fit || candidates[candidates.length - 1]).url
  }

  fail (reason) {
    this.log('fail — ' + (reason || 'unknown'))
    this.ready = false
    if (this.frame) { cancelAnimationFrame(this.frame); this.frame = null }
    this.releaseGL()
    if (this.canvas) this.canvas.remove()
    this.reveal()
  }

  build (sources) {
    const gl = this.gl
    if (!gl || gl.isContextLost()) return

    this.drawn = false
    this.releaseGL()

    if (this.observer) this.observer.disconnect()

    const vs = 'attribute vec2 aPos; attribute vec2 aUV; varying vec2 vUV;' +
      'void main(){ gl_Position = vec4(aPos,0.0,1.0); vUV = aUV; }'

    const fs = 'precision ' + (this.highpAvailable ? 'highp' : 'mediump') + ' float; varying vec2 vUV;' +
      'uniform sampler2D uFrom; uniform sampler2D uTo; uniform sampler2D uDisp;' +
      'uniform float uProgress; uniform float uIntensity; uniform float uBand;' +
      'uniform float uFromRatio; uniform float uToRatio; uniform float uBoxRatio;' +
      'float fold(float v){ return 1.0 - abs(1.0 - abs(v)); }' +
      'vec2 cover(vec2 uv, float imgRatio, float boxRatio){' +
      '  vec2 s = vec2(1.0);' +
      '  if (imgRatio > boxRatio) s.x = boxRatio / imgRatio; else s.y = imgRatio / boxRatio;' +
      '  return (uv - 0.5) * s + 0.5;' +
      '}' +
      'void main(){' +
      '  vec2 uv = vUV;' +
      '  float disp = texture2D(uDisp, uv).r;' +
      '  float drift = disp * uIntensity;' +
      '  vec2 uvFrom = cover(vec2(uv.x + uProgress * drift, uv.y), uFromRatio, uBoxRatio);' +
      '  vec2 uvTo = cover(vec2(uv.x - (1.0 - uProgress) * drift, uv.y), uToRatio, uBoxRatio);' +
      '  uvFrom = vec2(fold(uvFrom.x), fold(uvFrom.y));' +
      '  uvTo = vec2(fold(uvTo.x), fold(uvTo.y));' +
      '  vec4 f = texture2D(uFrom, uvFrom);' +
      '  vec4 t = texture2D(uTo, uvTo);' +
      '  float p = uProgress * (1.0 + 2.0 * uBand) - uBand;' +
      '  float m = smoothstep(disp - uBand, disp + uBand, p);' +
      // Textures are premultiplied, so mix in premultiplied space (keeps colour
      // true through the fade) then un-premultiply for the straight-alpha context.
      '  vec4 pm = mix(f, t, m);' +
      '  gl_FragColor = vec4(pm.a > 0.0 ? pm.rgb / pm.a : vec3(0.0), pm.a);' +
      '}'

    const prog = this.createProgram(vs, fs)
    if (!prog) return this.fail('program compile/link failed')
    gl.useProgram(prog)
    this.prog = prog

    const quad = new Float32Array([
      -1, -1, 0, 1, 1, -1, 1, 1, -1, 1, 0, 0,
      -1, 1, 0, 0, 1, -1, 1, 1, 1, 1, 1, 0,
    ])
    this.buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer)
    gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW)

    const aPos = gl.getAttribLocation(prog, 'aPos')
    const aUV = gl.getAttribLocation(prog, 'aUV')
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0)
    gl.enableVertexAttribArray(aUV)
    gl.vertexAttribPointer(aUV, 2, gl.FLOAT, false, 16, 8)

    this.uProgress = gl.getUniformLocation(prog, 'uProgress')
    this.uIntensity = gl.getUniformLocation(prog, 'uIntensity')
    this.uBand = gl.getUniformLocation(prog, 'uBand')
    this.uFromRatio = gl.getUniformLocation(prog, 'uFromRatio')
    this.uToRatio = gl.getUniformLocation(prog, 'uToRatio')
    this.uBoxRatio = gl.getUniformLocation(prog, 'uBoxRatio')

    this.ratios = sources.map(img => this.imageRatio(img))
    this.textures = sources.map(img => this.makeTexture(img))
    this.noise = this.makeTexture(this.makeNoise())

    if (this.debug) {
      sources.forEach((img, i) => {
        this.log('tex ' + i + ': ' + img.naturalWidth + '×' + img.naturalHeight)
      })
    }

    gl.uniform1i(gl.getUniformLocation(prog, 'uFrom'), 0)
    gl.uniform1i(gl.getUniformLocation(prog, 'uTo'), 1)
    gl.uniform1i(gl.getUniformLocation(prog, 'uDisp'), 2)
    gl.uniform1f(this.uIntensity, this.intensity)
    gl.uniform1f(this.uBand, this.band)
    gl.clearColor(0, 0, 0, 0)

    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, this.noise)

    this.boundFrom = -1
    this.boundTo = -1
    this.lastProgress = -1

    this.resize()
    this.ready = true

    this.observer = new ResizeObserver(this.resize)
    this.observer.observe(this.slider.container)
    this.render()
  }

  createProgram (vsSrc, fsSrc) {
    const gl = this.gl
    const compile = (type, src) => {
      const s = gl.createShader(type)
      gl.shaderSource(s, src)
      gl.compileShader(s)
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        this.log('shader compile error: ' + gl.getShaderInfoLog(s))
        gl.deleteShader(s)
        return null
      }
      return s
    }
    const v = compile(gl.VERTEX_SHADER, vsSrc)
    const f = compile(gl.FRAGMENT_SHADER, fsSrc)
    if (!v || !f) {
      if (v) gl.deleteShader(v)
      if (f) gl.deleteShader(f)
      return null
    }
    const p = gl.createProgram()
    gl.attachShader(p, v)
    gl.attachShader(p, f)
    gl.linkProgram(p)
    gl.deleteShader(v)
    gl.deleteShader(f)
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      this.log('program link error: ' + gl.getProgramInfoLog(p))
      gl.deleteProgram(p)
      return null
    }
    return p
  }

  makeTexture (src) {
    const gl = this.gl
    const t = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, t)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    // Premultiply on upload so the cross-fade composites cleanly (no grey ghost
    // where the two cut-outs differ) and edge filtering doesn't fringe. The noise
    // is opaque, so this is a no-op for it.
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.fitTexture(src))
    return t
  }

  /**
   * Guards against a CDN or hardware limit handing back something larger than
   * the texture budget, which would either fail to upload or blow the GPU cache.
   */
  fitTexture (src) {
    const width = src.naturalWidth || src.width
    const height = src.naturalHeight || src.height
    const largest = Math.max(width, height)
    const limit = Math.min(this.maxTexture, this.target)
    if (!largest || largest <= limit) return src

    const scale = limit / largest
    const scaled = document.createElement('canvas')
    scaled.width = Math.max(1, Math.round(width * scale))
    scaled.height = Math.max(1, Math.round(height * scale))
    scaled.getContext('2d').drawImage(src, 0, 0, scaled.width, scaled.height)
    return scaled
  }

  makeNoise () {
    const size = GalleryShader.noiseSize
    const c = document.createElement('canvas')
    c.width = c.height = size
    const x = c.getContext('2d')
    const id = x.createImageData(size, size)
    const d = id.data
    const base = 0.012 * (256 / size)
    const vnoise = (px, py) => {
      let s = 0, amp = 1, freq = base, tot = 0
      for (let o = 0; o < 4; o++) {
        const v = Math.sin(px * freq * 1.7 + o * 2.1) * Math.cos(py * freq * 1.3 + o * 1.3) +
          Math.sin((px + py) * freq * 0.9 + o)
        s += v * amp; tot += amp; amp *= 0.5; freq *= 2.0
      }
      return Math.max(0, Math.min(1, (s / tot) * 0.5 + 0.5))
    }
    for (let y = 0; y < size; y++) {
      for (let px = 0; px < size; px++) {
        const n = vnoise(px, y) * 255
        const i = (y * size + px) * 4
        d[i] = d[i + 1] = d[i + 2] = n
        d[i + 3] = 255
      }
    }
    x.putImageData(id, 0, 0)
    return c
  }

  resize () {
    const gl = this.gl
    if (!gl || gl.isContextLost()) return

    const box = this.slider.container.getBoundingClientRect()
    if (!(box.width > 0) || !(box.height > 0)) return

    const dpr = this.dpr
    const width = Math.round(box.width * dpr)
    const height = Math.round(box.height * dpr)

    if (width !== this.canvas.width || height !== this.canvas.height) {
      this.canvas.width = width
      this.canvas.height = height
      this.lastProgress = -1
    }

    gl.viewport(0, 0, width, height)
    gl.uniform1f(this.uBoxRatio, this.clampRatio(box.width / box.height))
    if (this.ready) this.render()
  }

  clampRatio (ratio) {
    if (!Number.isFinite(ratio) || ratio <= 0) return 1
    return Math.min(Math.max(ratio, GalleryShader.minRatio), GalleryShader.maxRatio)
  }

  imageRatio (img) {
    return img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 1
  }

  render () {
    if (!this.ready) return
    if (this.frame) return
    if (!this.visible || document.visibilityState !== 'visible') return
    this.frame = requestAnimationFrame(this.draw)
  }

  draw () {
    this.frame = null
    if (!this.ready) return

    const gl = this.gl
    if (!gl || gl.isContextLost()) return

    const details = this.slider.track?.details
    if (!details) return

    const max = details.maxIdx
    const progress = details.progress
    if (!Number.isFinite(max) || !Number.isFinite(progress) || max < 1) return

    const last = this.textures.length - 1
    if (last < 1) return

    const f = Math.max(0, Math.min(max, progress * max))
    const from = Math.max(0, Math.min(last, Math.floor(f)))
    const to = Math.max(0, Math.min(last, from + 1))
    const local = Math.max(0, Math.min(1, f - from))

    if (local === this.lastProgress && from === this.boundFrom && to === this.boundTo) return

    if (from !== this.boundFrom || to !== this.boundTo) {
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, this.textures[from])
      gl.activeTexture(gl.TEXTURE1)
      gl.bindTexture(gl.TEXTURE_2D, this.textures[to])
      gl.uniform1f(this.uFromRatio, this.ratios[from] ?? 1)
      gl.uniform1f(this.uToRatio, this.ratios[to] ?? 1)
      this.boundFrom = from
      this.boundTo = to
    }

    this.lastProgress = local
    gl.uniform1f(this.uProgress, local)

    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
    this.enhance()

    if (this.debug && !this.drawn) {
      this.drawn = true
      this.log('first draw ' + this.canvas.width + '×' + this.canvas.height +
        ' from=' + from + ' to=' + to +
        ' ratios=' + this.ratios[from] + '/' + this.ratios[to])
    }
  }

  handleContextLost () {
    this.log('context lost')
    this.ready = false
    if (this.frame) { cancelAnimationFrame(this.frame); this.frame = null }
    if (this.observer) this.observer.disconnect()
    this.textures = []
    this.noise = null
    this.prog = null
    this.buffer = null
    if (this.canvas) this.canvas.style.visibility = 'hidden'
    this.reveal()
  }

  handleContextRestored () {
    this.log('context restored — rebuilding')
    if (this.canvas) this.canvas.style.visibility = ''
    if (this.images) this.loadTextures()
  }

  releaseGL () {
    const gl = this.gl
    if (!gl || gl.isContextLost()) return

    this.textures.forEach(texture => gl.deleteTexture(texture))
    this.textures = []

    if (this.noise) { gl.deleteTexture(this.noise); this.noise = null }
    if (this.buffer) { gl.deleteBuffer(this.buffer); this.buffer = null }
    if (this.prog) { gl.deleteProgram(this.prog); this.prog = null }
  }

  reveal () {
    this.painted = false
    this.slider.container.classList.remove('is-ShaderEnhanced')
  }

  /** Deferred until the canvas holds a frame, so the slides never blank out. */
  enhance () {
    if (this.painted) return
    this.painted = true
    this.slider.container.classList.add('is-ShaderEnhanced')
  }

  log (msg) {
    if (!this.debug) return
    const line = '[shader] ' + msg
    console.log(line)
    if (!this.overlay) {
      this.overlay = document.createElement('div')
      this.overlay.className = 'prd-Media_ShaderDebug'
      Object.assign(this.overlay.style, {
        position: 'fixed',
        insetBlockStart: '0',
        insetInlineStart: '0',
        zIndex: '99999',
        maxBlockSize: '40vh',
        maxInlineSize: '90vw',
        overflow: 'auto',
        margin: '0',
        padding: '8px',
        font: '11px/1.4 monospace',
        color: '#0f0',
        background: 'rgba(0,0,0,0.8)',
        whiteSpace: 'pre-wrap',
        pointerEvents: 'none',
      })
      document.body.appendChild(this.overlay)
    }
    this.overlay.textContent += line + '\n'
  }

  destroy () {
    if (this.viewport) this.viewport.disconnect()
    if (this.observer) this.observer.disconnect()
    document.removeEventListener('visibilitychange', this.handleVisibility)
    if (this.frame) { cancelAnimationFrame(this.frame); this.frame = null }
    this.ready = false

    this.releaseGL()

    if (this.canvas) {
      this.canvas.removeEventListener('webglcontextlost', this.onContextLost)
      this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored)
      this.gl?.getExtension('WEBGL_lose_context')?.loseContext()
      this.canvas.remove()
    }

    this.gl = null
    if (this.overlay) this.overlay.remove()
    this.reveal()
  }

  get maxIdx () {
    return this.slider.track?.details?.maxIdx ?? 0
  }

  get dpr () {
    return Math.min(window.devicePixelRatio || 1, GalleryShader.maxDpr)
  }

  /** Pixel width the textures are sized for, capped to keep GPU memory sane. */
  get textureTarget () {
    const box = this.slider.container.getBoundingClientRect()
    const width = box.width > 0 ? box.width : window.innerWidth
    const wanted = Math.ceil(width * this.dpr)
    return Math.min(Math.max(wanted, GalleryShader.minTexture), GalleryShader.maxTextureTarget)
  }

  get intensity () {
    return this.options.intensity ?? 0.3
  }

  get band () {
    return this.options.band ?? 0.3
  }

  static get maxDpr () {
    return 2
  }

  static get minTexture () {
    return 512
  }

  static get maxTextureTarget () {
    return 1024
  }

  static get noiseSize () {
    return 128
  }

  static get minRatio () {
    return 0.1
  }

  static get maxRatio () {
    return 10
  }

  static get preloadMargin () {
    return '200px'
  }
}

/**
 * @param {{ intensity?: number, band?: number }} options
 */
const useShaderTransition = (options = {}) => {
  /** @param {KeenSliderInstance} slider */
  return (slider) => {
    const shader = new GalleryShader(slider, options)

    slider.on('created', () => shader.init())
    slider.on('detailsChanged', () => shader.render())
    slider.on('updated', () => shader.resize())
    slider.on('destroyed', () => shader.destroy())
  }
}

window.BAO = window.BAO || {}
window.BAO.Carousel = window.BAO.Carousel || {}
window.BAO.Carousel.plugins = window.BAO.Carousel.plugins || {}
window.BAO.Carousel.plugins.useShaderTransition = useShaderTransition
